import type {
  CellSig,
  ModelCell,
  SigElement,
  SigMeta,
  TableModel,
} from "./tableModel.types"

/**
 * Significance helpers shared by the on-screen table, the Excel exporter and
 * `describeCellSig`. Positions are model positions (`cells[row][col]`) unless
 * a parameter says otherwise.
 */

export type SigLevelName = "upper" | "lower"

export interface CellPosition {
  row: number
  col: number
}

const ownersCache = new WeakMap<TableModel, number[]>()
const sourcesCache = new WeakMap<TableModel, Map<string, CellPosition>>()

const positionKey = (row: number, col: number): string => `${row},${col}`

/** The banner-axis leaves of a model with significance (empty without it). */
const bannerLeafCount = (model: TableModel): number => {
  const meta = model.significance
  if (!meta) return 0
  return meta.bannerAxis === "across"
    ? model.across.leaves.length
    : model.down.leaves.length
}

/**
 * Significance element index of every banner-axis leaf, from the element
 * spans (-1 for a leaf no element covers). Cached per model: models are
 * never mutated after `buildTableModel`.
 */
export const bannerElementOwners = (model: TableModel): number[] => {
  const cached = ownersCache.get(model)
  if (cached) return cached
  const count = bannerLeafCount(model)
  const owners: number[] = Array(count).fill(-1)
  model.significance?.elements.forEach((element, index) => {
    const from = Math.max(0, element.span[0])
    const to = Math.min(count, element.span[1])
    for (let leaf = from; leaf < to; leaf++) owners[leaf] = index
  })
  ownersCache.set(model, owners)
  return owners
}

/** Index of the banner element a data cell belongs to, or -1. */
export const sigElementIndexAt = (
  model: TableModel,
  row: number,
  col: number
): number => {
  const meta = model.significance
  if (!meta) return -1
  const leaf = meta.bannerAxis === "across" ? col : row
  return bannerElementOwners(model)[leaf] ?? -1
}

/** The banner element a data cell belongs to, if any. */
export const sigElementAt = (
  model: TableModel,
  row: number,
  col: number
): SigElement | undefined => {
  const index = sigElementIndexAt(model, row, col)
  return index >= 0 ? model.significance?.elements[index] : undefined
}

/**
 * The strongest level of a "higher" result in a cell: its letters and an
 * upward vs-rest arrow. A cell that is only lower than the rest (▼) is not
 * tinted; its arrow says so.
 */
export const strongestSigLevel = (
  sig: CellSig | undefined
): SigLevelName | undefined => {
  if (!sig) return undefined
  const levels: SigLevelName[] = sig.letters.map((letter) => letter.level)
  if (sig.vsRest?.dir === "up") levels.push(sig.vsRest.level)
  if (levels.includes("upper")) return "upper"
  if (levels.includes("lower")) return "lower"
  return undefined
}

/** The confidence level (95, 90...) behind a level name. */
export const sigLevelPercent = (
  meta: SigMeta,
  level: SigLevelName
): number | undefined =>
  level === "upper" ? meta.levels[0] : (meta.levels[1] ?? meta.levels[0])

/**
 * Letters-row label of a banner element: "(A)", "(E)*" when low base
 * (with `marker`), "" for Totals or an element without a letter.
 */
export const bannerLetterLabel = (
  element: SigElement,
  marker: string
): string =>
  element.letter ? `(${element.letter})${element.lowBase ? marker : ""}` : ""

/** Letters of a tested cell grouped into runs of the same level, in banner order. */
export const letterRuns = (
  sig: CellSig
): { text: string; level: SigLevelName }[] => {
  const runs: { text: string; level: SigLevelName }[] = []
  sig.letters.forEach(({ letter, level }) => {
    const last = runs[runs.length - 1]
    if (last && last.level === level) last.text += letter
    else runs.push({ text: letter, level })
  })
  return runs
}

const cellAt = (
  model: TableModel,
  row: number,
  col: number
): ModelCell | undefined => model.cells[row]?.[col]

/**
 * Position of the `sig` cell printing a tested cell's letters: the engine's
 * `letters_at`, else the next letters cell along the statistic axis within
 * the same header element.
 */
export const lettersCellOf = (
  model: TableModel,
  row: number,
  col: number
): CellPosition | undefined => {
  const sig = cellAt(model, row, col)?.sig
  if (!sig) return undefined
  if (sig.lettersAt) return sig.lettersAt
  if (model.statAxis === "down") {
    const element = model.down.leaves[row]?.elementIndex
    for (let r = row + 1; r < model.cells.length; r++) {
      if (model.down.leaves[r]?.elementIndex !== element) break
      if (cellAt(model, r, col)?.role === "letters") return { row: r, col }
    }
    return undefined
  }
  const element = model.across.leaves[col]?.elementIndex
  for (let c = col + 1; c < (model.cells[row]?.length ?? 0); c++) {
    if (model.across.leaves[c]?.elementIndex !== element) break
    if (cellAt(model, row, c)?.role === "letters") return { row, col: c }
  }
  return undefined
}

/** Tested cell whose letters a `sig` cell prints, if any. */
export const sigSourceOf = (
  model: TableModel,
  row: number,
  col: number
): CellPosition | undefined => {
  let sources = sourcesCache.get(model)
  if (!sources) {
    sources = new Map()
    model.cells.forEach((cells, r) =>
      cells.forEach((cell, c) => {
        if (!cell.sig) return
        const at = lettersCellOf(model, r, c)
        if (at) sources!.set(positionKey(at.row, at.col), { row: r, col: c })
      })
    )
    sourcesCache.set(model, sources)
  }
  return sources.get(positionKey(row, col))
}

/**
 * The cell of banner element `element` that a cell is compared with: same
 * position across the banner, same statistic.
 */
export const peerCellOf = (
  model: TableModel,
  row: number,
  col: number,
  element: number
): CellPosition | undefined => {
  const meta = model.significance
  const own = cellAt(model, row, col)
  const target = meta?.elements[element]
  if (!meta || !own || !target) return undefined
  for (let leaf = target.span[0]; leaf < target.span[1]; leaf++) {
    const position =
      meta.bannerAxis === "across" ? { row, col: leaf } : { row: leaf, col }
    const cell = cellAt(model, position.row, position.col)
    if (cell && cell.stat === own.stat) return position
  }
  return undefined
}

/** What `sigLegendLines` needs from a table style. */
export interface SigLegendStyle {
  lowBase: { marker: string }
  sig: Record<SigLevelName, { fill: string }>
}

/** A fill that actually shows (not empty, "transparent" or "none"). */
const visibleFill = (fill: string | undefined): boolean =>
  typeof fill === "string" &&
  fill.trim() !== "" &&
  !/^(transparent|none)$/i.test(fill.trim())

/**
 * Display-only legend lines the client adds under the engine's legend, on
 * screen and in Excel (the engine's legend stays as it is):
 * - "* Base below 30: not tested." when a lettered banner element is low base
 *   (its letter carries the low-base `marker`);
 * - "Shaded: significantly higher." when a cell is tinted, i.e. its strongest
 *   "higher" result is at a level whose `fill` shows.
 */
export const sigDisplayLegendLines = (
  model: TableModel,
  style: SigLegendStyle
): string[] => {
  const meta = model.significance
  if (!meta) return []
  const lines: string[] = []
  if (meta.elements.some((element) => element.lowBase && element.letter)) {
    const marker = style.lowBase.marker.trim()
    lines.push(`${marker ? `${marker} ` : ""}Base below ${meta.minBase}: not tested.`)
  }
  const tinted = model.cells.some((cells) =>
    cells.some((cell) => {
      const level = strongestSigLevel(cell.sig)
      return level !== undefined && visibleFill(style.sig[level]?.fill)
    })
  )
  if (tinted) lines.push("Shaded: significantly higher.")
  return lines
}

/**
 * Every line written under a table with significance: the engine's legend,
 * then the display-only lines (`sigDisplayLegendLines`), then the notes as
 * "Note: ...".
 */
export const sigLegendLines = (
  model: TableModel,
  style: SigLegendStyle
): string[] => {
  const meta = model.significance
  if (!meta) return []
  return [
    ...meta.legend,
    ...sigDisplayLegendLines(model, style),
    ...meta.notes.map((note) => `Note: ${note}`),
  ]
}
